/**
 * Dynamisk programmering-baserad optimal batteristyrning.
 *
 * Algoritmen löser det fullständiga optimeringsproblemet exakt
 * (modulo SoC-diskretisering):
 *   minimera totalkostnad över hela perioden
 *   där varje timmes kostnad beror på import och export efter
 *   batteriets agerande, plus månadens fasta avgifter.
 *
 * Vi diskretiserar SoC i N nivåer (default 21 = 5%-steg). För varje
 * (timme, SoC-nivå) räknar vi minsta kostnad framåt rekursivt. Vid
 * simulering följer vi den optimala policyn.
 *
 * Komplexitet:
 *   - tid: O(T * N^2) där T = antal timmar, N = SoC-nivåer
 *   - minne: O(T * N) för värdetabell + O(T * N) för aktion-tabell
 *
 * För årsdata (8760 timmar, 21 nivåer): ~3.9 miljoner operationer.
 * Kör under en sekund i webbläsare.
 *
 * Begränsningar:
 *   - SoC diskretiserad: ger sub-optimal lösning men nära. 5% steg är
 *     tillräckligt för rätt riktningar.
 *   - Fasta månadsavgifter och tröskelmodellen inte med i DP-kostnaden.
 *     De är konstanta inom en månad och påverkar inte timbesluten.
 *   - Tier-byte (om batteriet flyttar dig över/under tröskel) är en
 *     andra-ordnings effekt som vi approximerar genom att räkna alla
 *     timmar med samma tariff.
 */

import type { HourPoint, BatteryConfig } from "./types";

export interface DPPolicy {
  /** För varje timme: optimal AC-handling i kWh. Positiv = nettoflöde
   *  IN i batteriet (laddning), negativ = nettoflöde UT (urladdning). */
  acFlow: number[];
  /** SoC efter varje timme för debugging. */
  socTrace: number[];
}

/**
 * Lös batterioptimeringen med backward induction.
 */
export function solveOptimalPolicy(
  hours: HourPoint[],
  battery: BatteryConfig,
  effectiveImportCost: (h: HourPoint) => number,
  effectiveExportRevenue: (h: HourPoint) => number,
  options?: { socLevels?: number; startSocKWh?: number },
): DPPolicy {
  const socLevels = options?.socLevels ?? 21;
  const T = hours.length;

  if (T === 0) {
    return { acFlow: [], socTrace: [] };
  }

  const chargeEff = Math.sqrt(battery.roundTripEfficiency);
  const dischargeEff = Math.sqrt(battery.roundTripEfficiency);
  const minSocFraction = battery.minSocFraction;
  const minSocKWh = battery.capacityKWh * minSocFraction;
  const maxSocKWh = battery.capacityKWh;

  // Mappa SoC-nivå (0..socLevels-1) till kWh
  const usableRange = maxSocKWh - minSocKWh;
  const socStep = usableRange / (socLevels - 1);
  const indexToSoc = (idx: number) => minSocKWh + idx * socStep;

  // Räkna AC-flöde för varje (currentSoc, nextSoc)-par och dess feasibility
  // AC_in (laddning) = delta_DC / chargeEff
  // AC_out (urladdning) = -delta_DC * dischargeEff
  const maxAcIn = battery.maxPowerKW; // kW under 1h = kWh
  const maxAcOut = battery.maxPowerKW;

  // value[t][i] = minsta kostnad från timme t med SoC-nivå i till slutet
  // För att spara minne sparar vi bara nuvarande och nästa timmes värden
  // medan vi bygger policy[t][i] = optimal next-SoC-index för varje (t, i)
  const policy: Int8Array[] = new Array(T);
  for (let t = 0; t < T; t++) {
    policy[t] = new Int8Array(socLevels);
  }

  // value vid slutet av perioden = 0 (vi värderar inte kvarvarande energi)
  let nextValue = new Float64Array(socLevels);

  // Backward induction
  for (let t = T - 1; t >= 0; t--) {
    const hour = hours[t]!;
    const importPrice = effectiveImportCost(hour);
    const exportRevenue = effectiveExportRevenue(hour);
    const baselineImport = hour.importKWh;
    const baselineExport = hour.exportKWh;

    const currentValue = new Float64Array(socLevels);

    for (let i = 0; i < socLevels; i++) {
      const currentSoc = indexToSoc(i);
      let bestCost = Number.POSITIVE_INFINITY;
      let bestNext = i;

      for (let j = 0; j < socLevels; j++) {
        const nextSoc = indexToSoc(j);
        const deltaDC = nextSoc - currentSoc;

        // Är denna övergång feasible?
        let acIn = 0;
        let acOut = 0;
        if (deltaDC > 0) {
          acIn = deltaDC / chargeEff;
          if (acIn > maxAcIn + 1e-9) continue;
        } else if (deltaDC < 0) {
          acOut = -deltaDC * dischargeEff;
          if (acOut > maxAcOut + 1e-9) continue;
        }

        // Räkna ut nettoflöden på AC-sidan denna timme.
        // acIn kan komma från solöverskott (gratis) eller från nätet (dyrt).
        // Vi tar solöverskott först.
        const acFromSolar = Math.min(acIn, baselineExport);
        const acFromGrid = acIn - acFromSolar;

        const netExport = Math.max(0, baselineExport - acFromSolar);
        const netImport = Math.max(
          0,
          baselineImport - acOut + acFromGrid,
        );

        const hourCost = netImport * importPrice - netExport * exportRevenue;
        const total = hourCost + nextValue[j]!;

        if (total < bestCost) {
          bestCost = total;
          bestNext = j;
        }
      }

      currentValue[i] = bestCost;
      policy[t]![i] = bestNext;
    }

    nextValue = currentValue;
  }

  // Forward pass: starta med angiven SoC eller minSoc som default
  const startSocKWh = options?.startSocKWh ?? minSocKWh;
  // Hitta närmaste diskreta SoC-index
  let currentSocIdx = Math.max(
    0,
    Math.min(
      socLevels - 1,
      Math.round((startSocKWh - minSocKWh) / socStep),
    ),
  );
  const acFlow: number[] = new Array(T);
  const socTrace: number[] = new Array(T);

  for (let t = 0; t < T; t++) {
    const currentSoc = indexToSoc(currentSocIdx);
    const nextSocIdx = policy[t]![currentSocIdx]!;
    const nextSoc = indexToSoc(nextSocIdx);
    const deltaDC = nextSoc - currentSoc;

    let acFlowThis: number;
    if (deltaDC > 0) {
      acFlowThis = deltaDC / chargeEff; // positivt = ladda
    } else if (deltaDC < 0) {
      acFlowThis = deltaDC * dischargeEff; // negativt = urladda
    } else {
      acFlowThis = 0;
    }

    acFlow[t] = acFlowThis;
    socTrace[t] = nextSoc;
    currentSocIdx = nextSocIdx;
  }

  return { acFlow, socTrace };
}
